'use client'

import useLocalStorageState from '@components/hooks/useLocalStorageState'
import Input from '@components/primitives/Input'
import { Text } from '@components/primitives/Text'
import { faLocation } from '@fortawesome/pro-regular-svg-icons/faLocation'
import {
  type AutoCompleteValues,
  autoCompleteLocalStorageKey,
} from '@modules/patientTrialApplications/autocomplete'
import { type ChangeEvent, useEffect } from 'react'
import { debounceTrackQuestionModified } from './trackQuestionModified'
import type { DemographicsCollectElementProps } from './types'

const debouncedEventTracking = debounceTrackQuestionModified('zipcode')

export default function CollectZipcode({
  formRef,
  patientProfileData: { zipcode },
  questionConfig,
  zipcodeFromIp,
}: DemographicsCollectElementProps) {
  const [autoCompleteValues] = useLocalStorageState<AutoCompleteValues>(
    autoCompleteLocalStorageKey,
    {},
  )

  const defaultZip =
    zipcode[0] ?? autoCompleteValues.zipcode ?? zipcodeFromIp ?? ''

  useEffect(() => {
    if (!defaultZip) {
      return
    }
    // need to wait for the input to be updated with the default value before dispatching an input event
    // this event is used to mark the form as "valid"
    setTimeout(() => formRef?.current?.dispatchEvent(new Event('input')), 20)
  }, [defaultZip])

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newInputValue = event.target.value
    debouncedEventTracking(newInputValue)
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='h3'
        value='What is your zipcode?'
      />
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='p-small'
        value='Enter your zipcode'
      />
      <Input
        autoComplete='postal-code'
        autoFocus={true}
        className='h-14 rounded-md border-neutral200 bg-neutral50 text-neutral900'
        defaultValue={defaultZip}
        icon={faLocation}
        name={questionConfig.id}
        onChange={handleInputChange}
        required={!questionConfig.skippable}
      />
    </div>
  )
}
